// Conversión de nodos IEC 61850 nativos a nodos genéricos por marca/referencia.
// La marca se deriva del IED seleccionado (vía IED_TO_DATASET -> dataset -> grupo).
// El contador es POR TIPO DE NODO y POR PESTAÑA: el índice "n" (1-based) lo calcula
// quien llama, contando cuántos nodos de ese tipo ya existen en la pestaña.

export type BrandGroup = "SIEMENS" | "SIEMENS_SP4" | "ABB620" | "ABB670" | "SEL";

// Mapa IED -> modelKey (dataset corto), inyectado desde el catálogo Python.
const IED_CATALOG: Record<string, Record<string, string[]>> = {"SIEMENS SP5": {"dataset_Ln1": ["7SL82", "7SL86", "7SL87", "7SA82", "7SA86", "7SA87"], "dataset_PTS1": ["7UT82", "7UT85"], "dataset_Mt1": ["7SK82", "7SK85", "7UM85"], "dataset_VI3p1": ["7SJ81", "7SJ82", "7SJ85"]}, "ABB/HITACHI": {"dataset_620s": ["REF620", "REM620", "RET620", "RER620", "REF615", "REM615", "RET615", "RED615", "REC615", "REX640"], "dataset_670s": ["REL670", "RED670", "RET670", "REG670", "REC670"]}, "SEL": {"dataset_G02_SEL": ["SEL411L", "SEL421"], "dataset_G03_SEL": ["SEL700BT", "SEL700G"], "dataset_G04_SEL": ["SEL751", "SEL787L"], "dataset_G08_SEL": ["SEL451"], "dataset_G09_SEL": ["SEL487B"], "dataset_G10_SEL": ["SEL487E"]}, "GE MULTILIN": {"dataset_GE": ["Familia UR"]}};
const DATASET_TO_MODEL: Record<string, string> = {"dataset_620s": "620s", "dataset_670s": "670s", "dataset_Ln1": "Ln1", "dataset_PTS1": "PTS1", "dataset_Mt1": "Mt1", "dataset_VI3p1": "VI3p1", "dataset_7SL82": "7SL82", "dataset_7UT82": "7UT82", "dataset_7SJ82": "7SJ82", "dataset_G02_SEL": "G02_SEL", "dataset_G03_SEL": "G03_SEL", "dataset_G04_SEL": "G04_SEL", "dataset_G08_SEL": "G08_SEL", "dataset_G09_SEL": "G09_SEL", "dataset_G10_SEL": "G10_SEL", "dataset_GE": "GE"};
export const SP_VIEWS: Record<string, string> = {"dataset_Ln1": "dataset_7SL82", "dataset_PTS1": "dataset_7UT82", "dataset_VI3p1": "dataset_7SJ82"};
export const BRANDS_SIN_MAPEO: string[] = ["SEL", "GE MULTILIN"];
// modelKey de cada dataset SP4 (para override de vista)
export const SP4_MODEL: Record<string, string> = Object.fromEntries(
  Object.entries(SP_VIEWS).map(([sp5, sp4]) => [sp5, DATASET_TO_MODEL[sp4] ?? ""])
);
// ¿el IED pertenece a una marca sin mapeo a SCL?
const IED_TO_BRAND: Record<string, string> = {};
for (const [brand, dsets] of Object.entries(IED_CATALOG)) {
  for (const ieds of Object.values(dsets)) {
    for (const ied of ieds) IED_TO_BRAND[ied] = brand;
  }
}
// Marcas cuyos IEDs SI admiten mapeo a SCL (para el desplegable del landing).
export const BRANDS_MAPEABLES: string[] =
  Object.keys(IED_CATALOG).filter(b => !BRANDS_SIN_MAPEO.includes(b));
export function iedAdmiteMapeo(iedRef: string): boolean {
  const b = IED_TO_BRAND[iedRef];
  return !!b && !BRANDS_SIN_MAPEO.includes(b);
}
const IED_TO_MODEL: Record<string, string> = {};
for (const dsets of Object.values(IED_CATALOG)) {
  for (const [ds, ieds] of Object.entries(dsets)) {
    for (const ied of ieds) IED_TO_MODEL[ied] = DATASET_TO_MODEL[ds] ?? "";
  }
}
export function modelKeyForIed(iedRef: string): string {
  return IED_TO_MODEL[iedRef] ?? "";
}

// Mapa dataset -> grupo de marca
const DATASET_TO_BRAND: Record<string, BrandGroup> = {
  Ln1: "SIEMENS", PTS1: "SIEMENS", Mt1: "SIEMENS", VI3p1: "SIEMENS",
  // Vista SP4 (modelos de datos de los reles 7xx8x recien anadidos)
  "7SJ82": "SIEMENS_SP4", "7SL82": "SIEMENS_SP4", "7UT82": "SIEMENS_SP4",
  "620s": "ABB620",
  "670s": "ABB670",
  G02_SEL: "SEL", G03_SEL: "SEL", G04_SEL: "SEL",
  G08_SEL: "SEL", G09_SEL: "SEL", G10_SEL: "SEL",
};

export function brandFromModelKey(modelKey: string): BrandGroup | null {
  return DATASET_TO_BRAND[modelKey] ?? null;
}

// Definición de un tipo de nodo genérico: etiqueta + generador del nodo n-ésimo (n>=1).
export interface GenericNodeType {
  id: string;
  label: string;          // etiqueta en español (se traduce vía i18n en la UI)
  gen: (n: number) => string;
}

// Helper: sufijo de DO que arranca en la base (n=1 sin número si la base no lo trae)
// y luego incrementa. Reproduce p.ej. SPS, SPS2, SPS3.
function doBareThen(base: string) {
  return (n: number) => (n <= 1 ? base : `${base}${n}`);
}
// Helper: DO con número desde n=1, ancho de ceros fijo. p.ej. Ind1.. / SPCSO01..
function doNumbered(prefix: string, width: number) {
  return (n: number) => `${prefix}${String(n).padStart(width, "0")}`;
}

export const GENERIC_NODES: Record<BrandGroup, GenericNodeType[]> = {
  SIEMENS: [
    { id: "si_din",  label: "Nodos de entradas digitales",            gen: (n) => `BinIO_BinaryInputs/LPDI${n}/In` },
    { id: "si_dout", label: "Nodos de salidas digitales",             gen: (n) => `BinIO_BinaryOutputs/LPDO${n}/OutSPS` },
    { id: "si_sps",  label: "Nodos genéricos de señales digitales",   gen: (n) => `UD1/GAPC1/${doBareThen("SPS")(n)}` },
    { id: "si_dps",  label: "Nodos genéricos de señales dobles",      gen: (n) => `UD1/GAPC1/${doBareThen("DPS")(n)}` },
    { id: "si_cmv",  label: "Nodos genéricos de medidas",             gen: (n) => `UD1/GAPC1/${doBareThen("CMV")(n)}` },
  ],
  SIEMENS_SP4: [
    { id: "sp4_din",  label: "Nodos de entradas digitales", gen: (n) => `CTRL/LPDI${n}/In` },
    { id: "sp4_dout", label: "Nodos de salidas digitales",  gen: (n) => `CTRL/LPDO${n}/OutSPS` },
    { id: "sp4_sps",  label: "Nodos genéricos de señales digitales", gen: (n) => `UD1/GAPC1/${doBareThen("SPS")(n)}` },
    { id: "sp4_dps",  label: "Nodos genéricos de señales dobles",    gen: (n) => `UD1/GAPC1/${doBareThen("DPS")(n)}` },
    { id: "sp4_cmv",  label: "Nodos genéricos de medidas",           gen: (n) => `UD1/GAPC1/${doBareThen("CMV")(n)}` },
  ],
  ABB620: [
    { id: "a620_ind",  label: "Nodos genéricos de señales digitales", gen: (n) => `LD0/SPCGAPC1/${doNumbered("Ind", 1)(n)}` },
    { id: "a620_spc",  label: "Nodos genéricos de control",           gen: (n) => `LD0/SPCGAPC1/${doNumbered("SPCSO", 1)(n)}` },
    { id: "a620_anin", label: "Nodos genéricos de medidas",           gen: (n) => `LD0/SCA4GAPC1/${doNumbered("AnIn", 1)(n)}` },
  ],
  ABB670: [
    { id: "a670_ind",  label: "Nodos genéricos de señales digitales", gen: (n) => `LD0/SP16GAPC/${doNumbered("Ind", 1)(n)}` },
    { id: "a670_spc",  label: "Nodos genéricos de control",           gen: (n) => `LD0/SPC8GAPC/${doNumbered("SPCSO", 1)(n)}` },
    { id: "a670_anin", label: "Nodos genéricos de medidas",           gen: (n) => `LD0/MVGAPC1/${doNumbered("AnIn", 1)(n)}` },
  ],
  SEL: [
    { id: "sel_spc",  label: "Nodos genéricos de control",            gen: (n) => `CON/RBGGIO1/${doNumbered("SPCSO", 2)(n)}` },
    { id: "sel_in",   label: "Nodos de entradas digitales",           gen: (n) => `ANN/INAGGIO1/${doNumbered("Ind", 2)(n)}` },
    { id: "sel_anin", label: "Nodos genéricos de medidas",            gen: (n) => `ANN/MVGGIO12/${doNumbered("AnIn", 2)(n)}` },
    { id: "sel_sv",   label: "Nodos de señales digitales",            gen: (n) => `ANN/SVGGIO3/${doNumbered("Ind", 2)(n)}` },
  ],
};

// Prefijo "estable" de un tipo (lo que NO cambia con n) para poder CONTAR
// cuántos nodos de ese tipo ya hay en la pestaña. Tomamos gen(1) y le quitamos
// la parte numérica final variable; para contar basta comparar el LD + clase.
export function typeSignature(t: GenericNodeType): string {
  // gen(1) y gen(2) difieren solo en la parte que crece; el prefijo común los identifica.
  const a = t.gen(1), b = t.gen(2);
  let i = 0; while (i < a.length && i < b.length && a[i] === b[i]) i++;
  return a.slice(0, i); // prefijo común (incluye LD/clase y, si crece el DO, el DO base)
}

// Cuenta cuántas filas ya tienen un nodo de este tipo (para saber el próximo índice).
// Devuelve el MAYOR índice k de este tipo presente en la pestaña (0 si no hay ninguno).
// El próximo nodo a asignar será maxIndexOfType(...) + 1, de modo que respeta
// ediciones manuales que hayan saltado números (p.ej. si existe Ind3, el siguiente es Ind4).
export function maxIndexOfType(mmsNodes: string[], t: GenericNodeType): number {
  const sig = typeSignature(t).split("/").slice(0, 2).join("/");
  let maxK = 0;
  for (const node of mmsNodes) {
    if (!node.startsWith(sig)) continue;
    for (let k = 1; k <= 9999; k++) {
      if (t.gen(k) === node) { if (k > maxK) maxK = k; break; }
      if (t.gen(k).length > node.length + 3) break;
    }
  }
  return maxK;
}
